// Click-driven data ops — Justin shouldn't have to open PowerShell + paste
// env vars to import v1 vendors / machines or to clean up demo data.
// These buttons wrap the same logic the scripts use but run in-browser as
// the signed-in user, so auth + Firebase rules just work.
//
// THREE BUTTONS:
//   1. Import v1 vendors — file picker → reads settings.vendors[] → createVendor
//   2. Import v1 machines — file picker → reads settings.rates[] → createMachine
//   3. Wipe demo + Gemma data — confirm → removes the 5 demo customers (codes
//      ACME/BETA/CCSP/DSMC/EVRR), 5 demo parts, 3 demo quotes, 1 demo order,
//      plus every /test/$runId subtree from battle-test runs.
//
// All three live on /dev so they're not in the main nav. Justin clicks once,
// watches the count climb, done.

import { useState } from 'react';
import { ref, get, remove } from 'firebase/database';
import { db } from '@/shared/firebase';
import { createVendor, listVendors } from '@/shared/vendors';
import type { CreateVendorInput } from '@/shared/vendors';
import { createMachine, listMachines } from '@/shared/machines';
import type { CreateMachineInput } from '@/shared/machines';
import type { VendorServiceType } from '@/shared/crmTypes';

// ──────────────────────────────────────────────────────────────────────────
// Vendor service mapping (v1 → v2 taxonomy). Mirrors scripts/import-v1-vendors.ts.
// ──────────────────────────────────────────────────────────────────────────

const SERVICE_MAP: Record<string, VendorServiceType> = {
  laser: 'laser_cutting',
  waterjet: 'waterjet_cutting',
  plasma: 'plasma_cutting',
  oxy_fuel: 'plasma_cutting',
  wire_edm: 'edm',
  anodize: 'anodizing',
  powder_coat: 'powder_coating',
  paint: 'painting',
  black_oxide: 'black_oxide',
  heat_treat: 'heat_treat',
  grinding: 'grinding',
  deburring: 'deburring',
  polishing: 'polishing',
  tumbling: 'tumbling',
  plating: 'plating_zinc',
  broaching: 'broaching',
  edm: 'edm',
  silk_screen: 'silk_screen',
  engraving: 'engraving',
  material: 'material_supply',
  machining: 'other',
};

interface V1Vendor {
  name?: string;
  contact?: string;
  email?: string;
  phone?: string;
  fax?: string;
  accountNum?: string;
  address?: string;
  notes?: string;
  services?: string[];
  cutProcesses?: string[];
  favorite?: boolean;
  defaultTerms?: string;
}

interface V1Rate {
  name?: string;
  rate?: number;
  runRate?: number;
  setupRate?: number;
}

function mapVendorServices(v: V1Vendor): VendorServiceType[] {
  const all = new Set<string>();
  for (const s of v.services ?? []) all.add(s);
  for (const s of v.cutProcesses ?? []) all.add(s);
  const mapped = new Set<VendorServiceType>();
  for (const raw of all) {
    const norm = raw.toLowerCase().replace(/[\s-]/g, '_');
    mapped.add(SERVICE_MAP[norm] ?? 'other');
  }
  return [...mapped];
}

function buildVendorNotes(v: V1Vendor): string | undefined {
  const parts: string[] = [];
  if (v.address) parts.push(`Address: ${v.address}`);
  if (v.accountNum) parts.push(`Acct #: ${v.accountNum}`);
  if (v.fax) parts.push(`Fax: ${v.fax}`);
  if (v.defaultTerms) parts.push(`Terms: ${v.defaultTerms}`);
  if (v.notes) parts.push(v.notes.trim());
  return parts.join('\n') || undefined;
}

// ──────────────────────────────────────────────────────────────────────────
// Wipe markers — must match scripts/wipe-test-data.ts exactly.
// ──────────────────────────────────────────────────────────────────────────

const DEMO_CUSTOMER_CODES = new Set(['ACME', 'BETA', 'CCSP', 'DSMC', 'EVRR']);
const DEMO_PART_NUMBERS = new Set([
  'BRK-2245-A',
  'PLT-1018-B',
  'WLD-FRM-001',
  'HYB-MNT-007',
  'BLT-91290-A',
]);
const DEMO_QUOTE_NUMBERS = new Set(['Q-1042', 'Q-1043', 'Q-1044']);
const DEMO_ORDER_NUMBERS = new Set(['O-2245']);

// ──────────────────────────────────────────────────────────────────────────

interface LogEntry {
  level: 'info' | 'ok' | 'warn' | 'error';
  text: string;
}

export function DevDataPanel() {
  const [busy, setBusy] = useState<null | 'vendors' | 'machines' | 'wipe'>(null);
  const [log, setLog] = useState<LogEntry[]>([]);

  function append(level: LogEntry['level'], text: string) {
    setLog((prev) => [...prev, { level, text }]);
  }
  function clearLog() {
    setLog([]);
  }

  async function handleImportVendors(file: File) {
    setBusy('vendors');
    clearLog();
    append('info', `Reading ${file.name}…`);
    try {
      const text = await file.text();
      const data = JSON.parse(text) as { settings?: { vendors?: V1Vendor[] } };
      const v1Vendors = data.settings?.vendors ?? [];
      append('info', `File contains ${v1Vendors.length} vendor(s).`);
      if (v1Vendors.length === 0) {
        append('warn', 'Nothing to import.');
        return;
      }
      append('info', 'Fetching existing vendors for dupe-skip…');
      const existing = await listVendors();
      const existingNames = new Set(existing.map((v) => v.name.toLowerCase().trim()));
      append('info', `Live collection has ${existing.length} vendor(s).`);

      let ok = 0;
      let skipped = 0;
      let failed = 0;
      for (const v of v1Vendors) {
        const name = (v.name ?? '').trim();
        if (!name) {
          skipped++;
          continue;
        }
        if (existingNames.has(name.toLowerCase())) {
          append('info', `⤳ skip dupe: ${name}`);
          skipped++;
          continue;
        }
        const input: CreateVendorInput = {
          name,
          services: mapVendorServices(v),
          contactName: v.contact?.trim() || undefined,
          contactEmail: v.email?.trim() || undefined,
          contactPhone: v.phone?.trim() || undefined,
          notes: buildVendorNotes(v),
          favorite: !!v.favorite,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          importedFromV1: true as any,
        };
        try {
          await createVendor(input);
          append(
            'ok',
            `✓ ${name}  [${input.services.join(', ') || 'no services'}]`,
          );
          ok++;
          existingNames.add(name.toLowerCase());
        } catch (e) {
          append('error', `✖ ${name}: ${(e as Error).message}`);
          failed++;
        }
      }
      append(
        'ok',
        `SUMMARY: ${ok} pushed · ${skipped} skipped · ${failed} failed`,
      );
    } catch (e) {
      append('error', `Fatal: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleImportMachines(file: File) {
    setBusy('machines');
    clearLog();
    append('info', `Reading ${file.name}…`);
    try {
      const text = await file.text();
      const data = JSON.parse(text) as { settings?: { rates?: V1Rate[] } };
      const v1Rates = data.settings?.rates ?? [];
      append('info', `File contains ${v1Rates.length} machine(s).`);
      if (v1Rates.length === 0) {
        append('warn', 'Nothing to import.');
        return;
      }
      append('info', 'Fetching existing machines for dupe-skip…');
      const existing = await listMachines();
      const existingNames = new Set(existing.map((m) => m.name.toLowerCase().trim()));
      append('info', `Live collection has ${existing.length} machine(s).`);

      let ok = 0;
      let skipped = 0;
      let failed = 0;
      for (const r of v1Rates) {
        const name = (r.name ?? '').trim();
        if (!name) {
          skipped++;
          continue;
        }
        if (existingNames.has(name.toLowerCase())) {
          append('info', `⤳ skip dupe: ${name}`);
          skipped++;
          continue;
        }
        const runRate = r.runRate ?? r.rate ?? 0;
        const setupRate = r.setupRate ?? r.rate ?? 0;
        const input: CreateMachineInput = {
          name,
          runRate,
          setupRate,
          active: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          importedFromV1: true as any,
        };
        try {
          await createMachine(input);
          append('ok', `✓ ${name}  [run $${runRate}/hr · setup $${setupRate}/hr]`);
          ok++;
          existingNames.add(name.toLowerCase());
        } catch (e) {
          append('error', `✖ ${name}: ${(e as Error).message}`);
          failed++;
        }
      }
      append(
        'ok',
        `SUMMARY: ${ok} pushed · ${skipped} skipped · ${failed} failed`,
      );
    } catch (e) {
      append('error', `Fatal: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleWipe() {
    if (
      !window.confirm(
        'Hard-delete the 5 demo customers (Acme/Beta/Cleveland/Dunbar/Everett),\n' +
          '5 demo parts, 3 demo quotes, 1 demo order, and every /test/$runId\n' +
          'battle-test subtree?\n\nThis is irreversible.',
      )
    ) {
      return;
    }
    setBusy('wipe');
    clearLog();
    try {
      let total = 0;

      // Customers
      const cSnap = await get(ref(db, 'customers'));
      const customers: Array<{ key: string; val: { code?: string; companyName?: string } }> = [];
      cSnap.forEach((child) => {
        customers.push({ key: child.key!, val: child.val() });
        return false;
      });
      const demoCust = customers.filter((c) => c.val.code && DEMO_CUSTOMER_CODES.has(c.val.code));
      append('info', `Customers: ${customers.length} total · ${demoCust.length} demo`);
      for (const c of demoCust) {
        append('ok', `✖ customer ${c.val.companyName ?? c.val.code}`);
        await remove(ref(db, `customers/${c.key}`));
        total++;
      }

      // Parts
      const pSnap = await get(ref(db, 'parts'));
      const parts: Array<{ key: string; val: { number?: string } }> = [];
      pSnap.forEach((child) => {
        parts.push({ key: child.key!, val: child.val() });
        return false;
      });
      const demoParts = parts.filter((p) => p.val.number && DEMO_PART_NUMBERS.has(p.val.number));
      append('info', `Parts: ${parts.length} total · ${demoParts.length} demo`);
      for (const p of demoParts) {
        append('ok', `✖ part ${p.val.number}`);
        await remove(ref(db, `parts/${p.key}`));
        total++;
      }

      // Quotes
      const qSnap = await get(ref(db, 'quotes'));
      const quotes: Array<{ key: string; val: { number?: string } }> = [];
      qSnap.forEach((child) => {
        quotes.push({ key: child.key!, val: child.val() });
        return false;
      });
      const demoQuotes = quotes.filter((q) => q.val.number && DEMO_QUOTE_NUMBERS.has(q.val.number));
      append('info', `Quotes: ${quotes.length} total · ${demoQuotes.length} demo`);
      for (const q of demoQuotes) {
        append('ok', `✖ quote ${q.val.number}`);
        await remove(ref(db, `quotes/${q.key}`));
        total++;
      }

      // Orders
      const oSnap = await get(ref(db, 'orders'));
      const orders: Array<{ key: string; val: { number?: string } }> = [];
      oSnap.forEach((child) => {
        orders.push({ key: child.key!, val: child.val() });
        return false;
      });
      const demoOrders = orders.filter((o) => o.val.number && DEMO_ORDER_NUMBERS.has(o.val.number));
      append('info', `Orders: ${orders.length} total · ${demoOrders.length} demo`);
      for (const o of demoOrders) {
        append('ok', `✖ order ${o.val.number}`);
        await remove(ref(db, `orders/${o.key}`));
        total++;
      }

      // Test runs
      const tSnap = await get(ref(db, 'test'));
      const runs: Array<string> = [];
      tSnap.forEach((child) => {
        runs.push(child.key!);
        return false;
      });
      append('info', `Test runs: ${runs.length}`);
      for (const k of runs) {
        append('ok', `✖ /test/${k}`);
        await remove(ref(db, `test/${k}`));
        total++;
      }

      append('ok', `WIPE COMPLETE — ${total} record(s) / subtree(s) removed.`);
    } catch (e) {
      append('error', `Fatal: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  function pickFile(handler: (file: File) => void) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) handler(f);
    };
    input.click();
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <p className="text-xs text-text-mute mb-3">
        Click-driven data ops. No PowerShell, no env vars. Same logic as the
        <code className="bg-bg px-1 rounded mx-1">scripts/import-v1-*</code>
        and
        <code className="bg-bg px-1 rounded mx-1">scripts/wipe-test-data</code>
        files — runs in-browser as the signed-in user.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => pickFile(handleImportVendors)}
          disabled={!!busy}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
        >
          {busy === 'vendors' ? 'Importing vendors…' : '+ Import v1 vendors (.json)'}
        </button>
        <button
          type="button"
          onClick={() => pickFile(handleImportMachines)}
          disabled={!!busy}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
        >
          {busy === 'machines' ? 'Importing machines…' : '+ Import v1 machines (.json)'}
        </button>
        <button
          type="button"
          onClick={handleWipe}
          disabled={!!busy}
          className="px-3 py-1.5 border border-orange/40 text-orange text-sm rounded font-semibold hover:bg-orange/10 disabled:opacity-50"
        >
          {busy === 'wipe' ? 'Wiping…' : '✖ Wipe demo + Gemma data'}
        </button>
      </div>

      <p className="text-[11px] text-text-mute mb-2">
        For the import buttons, pick your{' '}
        <code className="bg-bg px-1 rounded">keystone-quoting-backup-…json</code>{' '}
        file from Downloads.
      </p>

      {log.length > 0 && (
        <div className="bg-bg border border-line-dark rounded p-3 max-h-96 overflow-y-auto font-mono text-[11px] leading-relaxed">
          {log.map((entry, i) => (
            <div
              key={i}
              className={
                entry.level === 'error'
                  ? 'text-orange'
                  : entry.level === 'warn'
                    ? 'text-amber'
                    : entry.level === 'ok'
                      ? 'text-green'
                      : 'text-text-mute'
              }
            >
              {entry.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
